/*
 * Shop customer auth — email + password JWT.
 *
 * Token in localStorage under `lumenforge_shop_token`, sent as
 * `Authorization: Bearer` on every request. Modal-driven UX — no OAuth
 * redirects. See <SignInModal /> for the actual form.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "lumenforge_shop_token";
const ShopAuthContext = createContext(null);

function normalisedErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function ShopAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("signin"); // "signin" | "signup"
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  const persistToken = useCallback((t) => {
    setToken(t);
    try {
      if (t) window.localStorage.setItem(TOKEN_KEY, t);
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/shop-auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUser(await res.json());
      else { persistToken(null); setUser(null); }
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, [token, persistToken]);

  useEffect(() => { refresh(); }, [refresh]);

  const openSignIn = useCallback((mode = "signin") => { setModalMode(mode); setModalOpen(true); }, []);
  const closeSignIn = useCallback(() => setModalOpen(false), []);

  const signIn = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/shop-auth/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(normalisedErrorDetail(data.detail) || "Sign-in failed");
    persistToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, [persistToken]);

  const signUp = useCallback(async (email, password, name) => {
    const res = await fetch(`${API}/api/shop-auth/register`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(normalisedErrorDetail(data.detail) || "Sign-up failed");
    persistToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, [persistToken]);

  const signInWithGoogle = useCallback(() => new Promise((resolve, reject) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/shop-callback";
    const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    const w = 520, h = 680;
    const left = Math.max(0, window.screenX + (window.innerWidth - w) / 2);
    const top = Math.max(0, window.screenY + (window.innerHeight - h) / 2);
    const popup = window.open(url, "lumenforge-google-signin", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) { reject(new Error("Popup blocked. Enable popups for this site to sign in with Google.")); return; }

    let done = false;
    const cleanup = () => { window.removeEventListener("message", onMessage); clearInterval(closedPoll); };
    const onMessage = async (ev) => {
      if (ev.origin !== window.location.origin) return;
      if (!ev.data || ev.data.type !== "lumenforge-google-session") return;
      if (done) return;
      done = true;
      try {
        const res = await fetch(`${API}/api/shop-auth/google-session`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: ev.data.session_id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(normalisedErrorDetail(data.detail) || "Sign-in failed");
        persistToken(data.access_token);
        setUser(data.user);
        cleanup();
        try { popup.close(); } catch { /* ignore */ }
        resolve(data.user);
      } catch (e) {
        cleanup();
        reject(e);
      }
    };
    window.addEventListener("message", onMessage);
    const closedPoll = setInterval(() => {
      if (popup.closed && !done) {
        done = true; cleanup(); reject(new Error("Sign-in was cancelled"));
      }
    }, 500);
  }), [persistToken]);

  const signOut = useCallback(async () => {
    // Instant sign-out UX: clear local state first, fire-and-forget backend
    // logout in the background. JWTs aren't invalidated server-side anyway
    // (no blacklist for this app) so waiting on the network round-trip just
    // creates a perceived lag with no security benefit.
    const t = token;
    persistToken(null);
    setUser(null);
    if (t) {
      fetch(`${API}/api/shop-auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    }
  }, [token, persistToken]);

  const deleteAccount = useCallback(async () => {
    if (!token) throw new Error("Not signed in");
    const res = await fetch(`${API}/api/shop-auth/me`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("Delete failed");
    persistToken(null); setUser(null);
  }, [token, persistToken]);

  const authFetch = useCallback((url, opts = {}) => {
    const headers = { ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  }, [token]);

  const value = useMemo(() => ({
    user, loading, isAuthenticated: !!user,
    modalOpen, modalMode, openSignIn, closeSignIn, setModalMode,
    signIn, signUp, signInWithGoogle, signOut, deleteAccount, authFetch,
  }), [user, loading, modalOpen, modalMode, openSignIn, closeSignIn, signIn, signUp, signInWithGoogle, signOut, deleteAccount, authFetch]);

  return <ShopAuthContext.Provider value={value}>{children}</ShopAuthContext.Provider>;
}

export function useShopAuth() {
  const ctx = useContext(ShopAuthContext);
  if (!ctx) throw new Error("useShopAuth must be used inside <ShopAuthProvider>");
  return ctx;
}
